import {ReactElement, useEffect, useState} from "react";
import {PropertyType, useUpdateManualPropertyMutation} from "../../../__generated__/generated_types";
import {Close, Modal} from "@zendeskgarden/react-modals";
import {toast} from "react-toastify";
import ManualPropertyForm, {TManualPropertyRecord} from "./components/manual-property-form/ManualPropertyForm";
import {ThemeProvider} from "styled-components";
import {ViziblyTheme} from "../../../styles/zendesk-garden/ViziblyZDGTheme";
import {ManualProperty, PMSProperty} from "./logic/types";

export interface IUpdateManualProperty {
    manualProperty: ManualProperty;
    pmsPropertiesData: PMSProperty[] | undefined;
    existingPropertiesData: ManualProperty[] | undefined;
    formVisible: boolean;
    onModalClose: () => void;
    onUpdate: () => void;
    budgetYear: number;
}

interface ManualPropertyState extends ManualProperty {
    isSystemNameSameAsName: boolean;
}

export function ManualPropertyUpdateModal(props: IUpdateManualProperty): ReactElement {
    const {
        manualProperty,
        pmsPropertiesData,
        formVisible,
        onModalClose,
        onUpdate,
    } = props;

    const [propertyData, setPropertyData] = useState<ManualPropertyState>({...manualProperty, isSystemNameSameAsName: manualProperty.systemName == manualProperty.name});

    const [updateManualProperty, {loading: updateLoading}] = useUpdateManualPropertyMutation({
        notifyOnNetworkStatusChange: true
    });

    useEffect(() => {
        setPropertyData({...manualProperty, isSystemNameSameAsName: manualProperty.systemName == manualProperty.name});
    }, [manualProperty, formVisible]);

    function updateManualPropertyState(update: Partial<TManualPropertyRecord>): void {
        if(propertyData) {
            setPropertyData({
                ...propertyData,
                ...update,
            });
        }
    }

    function reset(): void {
        onModalClose();
    }

    function doSaveManualProperty(): void {
        if (propertyData && propertyData.id) {
            let systemName: string|undefined = undefined;
            if (propertyData.propertyType === PropertyType.Standard) {
                if (propertyData.isSystemNameSameAsName) {
                    systemName = propertyData.name;
                }
                else {
                    systemName = propertyData.systemName;
                }
            }
            updateManualProperty({
                variables: {
                    id: propertyData.id,
                    name: propertyData.name,
                    systemName: systemName,
                    reforecastStartMonthIndex: propertyData.reforecastStartMonthIndex,
                    isDeactivated: propertyData.isDeactivated,
                    budgetYear: props.budgetYear
                }
            }).then(data => {
                if (data.data?.updateManualProperty) {
                    toast.success(`Manual property "${data.data.updateManualProperty.name}" updated`);
                    onUpdate();
                    reset();
                } else {
                    throw new Error("Unknown Error");
                }
            }).catch(() => {
                toast.error("Could not update property.");
            });
        }
    }

    if (!formVisible) {
        return <></>;
    }

    return (
        <ThemeProvider theme={ViziblyTheme}>
            <Modal onClose={reset} isLarge style={{minWidth: "80rem"}}>
                {
                    manualProperty &&
                    <ManualPropertyForm
                        isStandard={propertyData.propertyType == PropertyType.Standard}
                        manualProperty={propertyData}
                        loading={updateLoading}
                        onChange={updateManualPropertyState}
                        onSubmit={doSaveManualProperty}
                        onClose={reset}
                        pmsPropertiesData={pmsPropertiesData}
                        existingProperties={props.existingPropertiesData}
                    />
                }
                <Close aria-label="Close modal" />
            </Modal>
        </ThemeProvider>
    );
}
